import React, { useState, useEffect } from "react";
import "./Login.css";
import styles from "./Auth.module.css";
import { Eye, EyeOff } from "lucide-react";
import { NavLink, useNavigate, useSearchParams } from "react-router-dom";
import { authAPI } from "../../api/apiHelper";
import useToast from "../../hooks/useToast";
import Toast from "../../components/Toast";
import dec from "../../assets/landing/dec.svg";
import { Logo } from "../../components/logo/Logo";

export const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [token, setToken] = useState("");
  const navigate = useNavigate();
  const { toast, success, error, hideToast } = useToast();

  useEffect(() => {
    const tokenFromUrl = searchParams.get("token");
    if (!tokenFromUrl) {
      error("Invalid or missing reset token.", { title: "Error" });
      setTimeout(() => navigate("/login"), 2000);
    } else {
      setToken(tokenFromUrl);
    }
  }, [searchParams, navigate, error]);

  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (!password || !confirmPassword) {
      error("Please fill in all fields.", { title: "Validation Error" });
      return;
    }

    if (password.length < 8) {
      error("Password must be at least 8 characters long.", {
        title: "Validation Error",
      });
      return;
    }

    if (password !== confirmPassword) {
      error("Passwords do not match.", { title: "Validation Error" });
      return;
    }

    setIsLoading(true);
    try {
      await authAPI.resetPassword(token, password);
      success("Password reset successful! Redirecting to login...", {
        title: "Success",
      });
      setTimeout(() => {
        navigate("/login");
      }, 2000);
    } catch (err) {
      console.error("Reset password error:", err);
      if (err.response_code === 400) {
        error(err.message || "Reset link expired. Please request a new one.", {
          title: "Link Expired",
        });
      } else {
        error(err.message || "Failed to reset password. Please try again.", {
          title: "Error",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-root">
      <Toast
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        title={toast.title}
        duration={toast.duration}
        onClose={hideToast}
        position={toast.position}
      />
      <div className="login-left-panel">
        <NavLink to="/">
          <Logo
            variant="white authLogo"
            className={styles.logo}
          />
        </NavLink>
        <img src={ dec } alt="Decorative background shape" className="svg_style" />
        <img src={ dec } alt="Decorative background shape" className="svg_style_1" />
        <div className="login-features">
          <div className="login-feature">
            <span className="login-check">✔</span>
            <div>
              <div className="login-feature-title">Seamless Transcription</div>
              <div className="login-feature-desc">
                Effortlessly transcribe audio to text with our advanced AI
                technology
              </div>
            </div>
          </div>
          <div className="login-feature">
            <span className="login-check">✔</span>
            <div>
              <div className="login-feature-title">Multi-language Support</div>
              <div className="login-feature-desc">
                Transcribe conversations in multiple languages with ease
              </div>
            </div>
          </div>
          <div className="login-feature">
            <span className="login-check">✔</span>
            <div>
              <div className="login-feature-title">Collaboration Tools</div>
              <div className="login-feature-desc">
                Collaborate with team members by sharing and annotating
                transcripts in real-time
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="login-right-panel">
        <form className="login-form" onSubmit={handleResetPassword}>
          <div className="login-title">Reset Password</div>
          <div className="login-subtitle">
            Enter your new password below
          </div>

          <div className="login-label">New Password</div>
          <div style={{ position: "relative" }}>
            <input
              className="login-input"
              type={showPassword ? "text" : "password"}
              placeholder="8+ characters required"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={isLoading}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              style={{
                position: "absolute",
                right: "10px",
                top: "35%",
                transform: "translateY(-50%)",
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "#6b7280",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "0"
              }}
              disabled={isLoading}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>

          <div className="login-label">Confirm New Password</div>
          <div style={{ position: "relative" }}>
            <input
              className="login-input"
              type={showConfirmPassword ? "text" : "password"}
              placeholder="Confirm your password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              disabled={isLoading}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              style={{
                position: "absolute",
                right: "10px",
                top: "35%",
                transform: "translateY(-50%)",
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "#6b7280",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "0"
              }}
              disabled={isLoading}
              aria-label={
                showConfirmPassword ? "Hide password" : "Show password"
              }
            >
              {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>

          <button
            type="submit"
            className="login-submit-btn"
            disabled={isLoading}
          >
            {isLoading ? "Resetting..." : "Reset Password"}
          </button>

          <div className="login-signup-row">
            Remember your password?{" "}
            <NavLink to="/login" className="login-signup-link">
              Sign in
            </NavLink>
          </div>
        </form>
      </div>
    </div>
  );
};
